import {
  useOrganizationSOWs,
  useProducts,
} from '@airship-platform/airship-proposal';
import { useCallback, useMemo } from 'react';
import { useAppointmentProposalOptionModifiers } from '../../../data-access/appointmentProposalOptionModifiers';
import { useAppointmentProposalOptionProducts } from '../../../data-access/appointmentProposalOptionProducts';
import {
  AppointmentProposalOptionCreate,
  useAppointmentProposalOptions,
} from '../../../data-access/appointmentProposalOptions';
import { useAppointmentProposalOptionSOWs } from '../../../data-access/appointmentProposalOptionSows';
import { AppointmentProposal } from '../../../data-access/appointmentProposals';
import { useApplyProposalTemplateHttp } from './useApplyProposalTemplateHttp';

type ProposalOptionDefaults = Partial<AppointmentProposalOptionCreate> & {
  name: AppointmentProposalOptionCreate['name'];
};

type ProposalIds = Pick<
  AppointmentProposal,
  'appointmentId' | 'jobId' | 'appointmentProposalId'
>;

export const useApplyProposalTemplate = () => {
  const { appendTemplateOptions } = useApplyProposalTemplateHttp();
  const {
    options: appointmentOptions,
    createAppointmentProposalOption,
    updateAppointmentProposalOption,
  } = useAppointmentProposalOptions();
  const { appendModifiers } = useAppointmentProposalOptionModifiers();
  const { appendProducts } = useAppointmentProposalOptionProducts();
  const { appendSOWs } = useAppointmentProposalOptionSOWs();

  const { availableDiscounts, availableFees, availableRebates } = useProducts();
  const { sows } = useOrganizationSOWs();

  const lookupMaps = useMemo(
    () => ({
      modifiers: new Map(
        [availableDiscounts, availableFees, availableRebates]
          .flat()
          .map((m) => [m.discountsAndFeesId, m])
      ),
      sows: new Map(sows.map((sow) => [sow.sowId, sow])),
    }),
    [availableDiscounts, availableFees, availableRebates, sows]
  );

  const getNextOptionSortOrder = useCallback(
    (offset?: number) =>
      (offset || 0) +
      Math.max(0, ...appointmentOptions.map((o) => o.sortOrder + 1)),
    [appointmentOptions]
  );

  const createOption = useCallback(
    async (currentProposal: ProposalIds, optionData: ProposalOptionDefaults) =>
      createAppointmentProposalOption(
        currentProposal.appointmentId,
        currentProposal.appointmentProposalId,
        {
          jobId: currentProposal.jobId,
          optionType: 'TEMPLATE',
          ...optionData,
          name: optionData.name,
          sortOrder: getNextOptionSortOrder(optionData.sortOrder),
        }
      ),
    [getNextOptionSortOrder, createAppointmentProposalOption]
  );

  const appendOptions = useCallback(
    async (
      appointmentId: string,
      appointmentProposalId: string,
      payload: {
        options: {
          proposalTemplateOptionId: string;
          appointmentProposalOptionId: string;
        }[];
        optionDefaults: {
          appointmentProposalOptionId: string;
          summary?: string;
          summaryTextPlain?: string;
        }[];
      }
    ) => {
      const res = await appendTemplateOptions(
        appointmentId,
        appointmentProposalId,
        { options: payload.options }
      );

      await Promise.all(
        payload.optionDefaults.map((o) =>
          updateAppointmentProposalOption(
            appointmentId,
            appointmentProposalId,
            o.appointmentProposalOptionId,
            { summary: o.summary, summaryTextPlain: o.summaryTextPlain }
          )
        )
      );

      appendProducts(res.products);
      appendModifiers(res.modifiers);
      appendSOWs(res.sows);
    },
    [
      appendModifiers,
      appendProducts,
      appendSOWs,
      appendTemplateOptions,
      updateAppointmentProposalOption,
    ]
  );

  return {
    lookupMaps,
    createOption,
    appendOptions,
  };
};
